import type { TFunction } from "i18next";

import { DateRangeQuickFilters } from "@/components/ui/v2/date-range-picker";
import { URL_PARAMS } from "@/constants/urlParams";
import type { ModuleUiColumnDefinition, UiComponent } from "@/features/integrations/types";

// ---------------------------------------------------------------------------
// Date defaults
// ---------------------------------------------------------------------------

const today = new Date();
const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
const oneWeekAgo = new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000);

/** Default start date when no URL param (one week ago). */
export const DEFAULT_START_DATE = oneWeekAgo;

/** Default end date when no URL param (today). */
export const DEFAULT_END_DATE = today;

// ---------------------------------------------------------------------------
// Date range picker
// ---------------------------------------------------------------------------

export const DATE_RANGE_PICKER_CONFIG = {
  allowedPresetIds: DateRangeQuickFilters.all,
};

// ---------------------------------------------------------------------------
// Metrics (total applies card)
// ---------------------------------------------------------------------------

export const METRICS_CONFIG = {
  totalApplies: {
    titleKey: "integrations.cockpit.applies.totalApplies" as const,
    subtitleKey: "integrations.cockpit.applies.appliesInPeriod" as const,
  },
  distinctJobBoards: {
    titleKey: "integrations.cockpit.applies.distinctJobBoards" as const,
    subtitleKey: "integrations.cockpit.applies.jobBoardsInPeriod" as const,
  },
  distinctClients: {
    titleKey: "integrations.cockpit.applies.distinctClients" as const,
    subtitleKey: "integrations.cockpit.applies.clientsInPeriod" as const,
  },
} as const;

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

export const CHART_CONFIG = {
  jobBoardChart: {
    titleKey: "integrations.cockpit.applies.appliesPerJobBoard" as const,
    subtitleKey: "integrations.cockpit.applies.appliesPerJobBoardSubtitle" as const,
  },
} as const;

// ---------------------------------------------------------------------------
// Filter drawer
// ---------------------------------------------------------------------------

const ALL_FILTER_KEYS = [
  "client",
  "integration",
  "applicationSourceType",
  "publicationId",
  "matchId",
  "candidateId",
] as const;
const CLIENT_FILTER_KEYS = [
  "integration",
  "applicationSourceType",
  "publicationId",
  "matchId",
  "candidateId",
] as const;

export const APPLIES_FILTER_KEYS = ALL_FILTER_KEYS;

export function getAppliesFilterKeys(clientScoped: boolean) {
  return clientScoped ? CLIENT_FILTER_KEYS : ALL_FILTER_KEYS;
}

export const APPLIES_FILTER_PRESERVE_PARAM_KEYS = [
  URL_PARAMS.ORDER_BY,
  URL_PARAMS.DESCENDING,
  URL_PARAMS.PAGE,
  URL_PARAMS.PAGE_SIZE,
  URL_PARAMS.SEARCH,
  "startDate",
  "endDate",
] as const;

// ---------------------------------------------------------------------------
// Page / search
// ---------------------------------------------------------------------------

export const PAGE_CONFIG = {
  titleKey: "common.applies" as const,
  subtitleKey: "integrations.cockpit.applies.subtitle" as const,
  searchPlaceholderKey: "integrations.cockpit.applies.searchApplies" as const,
};

// ---------------------------------------------------------------------------
// Table toolbar
// ---------------------------------------------------------------------------

export const TABLE_TOOLBAR_CONFIG = {
  // Names what the search actually matches. The backend search is a LIKE across campaign name, client
  // name, publication id, integration display name, and the RecruitNow match / ATS candidate ids — so
  // "Search publication ID" understated it by five fields.
  searchPlaceholderKey: "integrations.cockpit.applies.searchApplies" as const,
  dateRangeFilterKey: "dateRange" as const,
  rowIdColumn: "id" as const,
};

// ---------------------------------------------------------------------------
// Table columns
// ---------------------------------------------------------------------------

const ALL_COLUMNS: ModuleUiColumnDefinition[] = [
  { key: "dateSubmitted", label: "common.dateSubmitted", type: "DateTime" },
  { key: "clientName", label: "common.client", type: "String", showNullAs: "N/A" },
  { key: "jobBoardName", label: "common.integration", type: "String", showNullAs: "N/A" },
  { key: "publicationId", label: "common.publicationId", type: "String", showNullAs: "N/A" },
  {
    key: "applicationSourceTypes",
    label: "integrations.cockpit.applies.detail.applicationSources",
    type: "String",
    showNullAs: "N/A",
    sortable: false,
  },
];

// Client-scoped view drops the client column and renders the publication ID as a deep link, using the
// row's `jobSourceUrl` (built server-side; null on tenant/cross-client views → plain text).
const CLIENT_COLUMNS: ModuleUiColumnDefinition[] = ALL_COLUMNS
  .filter((c) => c.key !== "clientName")
  .map((c) => (c.key === "publicationId" ? { ...c, type: "Link", hrefKey: "jobSourceUrl" } : c));

export function getAppliesColumns(clientScoped: boolean) {
  return clientScoped ? CLIENT_COLUMNS : ALL_COLUMNS;
}

// ---------------------------------------------------------------------------
// UI component tree (table toolbar + data table)
// ---------------------------------------------------------------------------

/** Builds the Applies page UI component array. Requires `t` for translated placeholder. */
export function getAppliesUiComponents(t: TFunction, clientScoped = false): UiComponent[] {
  return [
    {
      component: "tableToolbar",
      props: {
        search: {
          placeholder: t(TABLE_TOOLBAR_CONFIG.searchPlaceholderKey),
          enabled: true,
        },
        filters: [
          {
            key: TABLE_TOOLBAR_CONFIG.dateRangeFilterKey,
            type: "DateRange",
            inlineWithSearch: true,
          },
        ],
      },
    },
    {
      component: "dataTable",
      props: {
        rowIdColumn: TABLE_TOOLBAR_CONFIG.rowIdColumn,
        columns: getAppliesColumns(clientScoped),
      },
    },
  ];
}
